import {
    ApiDocumentTypes,
    ApiFileTypes,
    type ApiBounds,
    type ApiFile,
    type ApiReference,
} from "../shared/interfaces";
import { attachmentKind } from "../util/attachmentKind";

export type ReferenceCategory = "drawing" | "spec" | "other";

export function categorizeReference(reference: ApiReference, file: ApiFile): ReferenceCategory {
    const documentType = reference.documentType;
    if (
        documentType === ApiDocumentTypes.Drawing ||
        file.fileType === ApiFileTypes.Drawing ||
        file.fileType === ApiFileTypes.Sheet
    ) {
        return "drawing";
    }
    if (
        documentType === ApiDocumentTypes.CSI ||
        documentType === ApiDocumentTypes.AzDOT ||
        documentType === ApiDocumentTypes.TxDOT ||
        file.fileType === ApiFileTypes.Spec
    ) {
        return "spec";
    }
    return "other";
}

const ENTITY_ID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const PARENTHETICAL_WITH_ENTITY_ID = new RegExp(`\\s*\\([^()]*${ENTITY_ID.source}[^()]*\\)`, "gi");
const SHEET_CODE = /^((?=[A-Z0-9.-]*\d)[A-Z0-9.-]+)(?:\s|\(|$)/i;

/**
 * A section-path part as people may read it: never an entity id. Some stored
 * references name the revision they were read from, e.g. "MA011 (revision <uuid>)";
 * that parenthetical goes, and a part that still names an id afterwards is not shown.
 */
export function readablePathPart(part: string): string | null {
    const readable = part.replace(PARENTHETICAL_WITH_ENTITY_ID, "").trim();
    return readable && !ENTITY_ID.test(readable) ? readable : null;
}

/**
 * A drawing reference as a sheet code and its titles, broadest first. The code is the
 * leading sheet code of the path's first part, else the file name's ("A-101 - Plan.pdf").
 * Every title is read once: without the sheet code it repeats ("MA001 CODE ANALYSIS"),
 * without wrapping parentheses ("I1.101 (ENLARGED PLAN)"), never an id, and never the
 * same words as a broader title.
 */
export function readDrawingPath(
    sectionPath: string[] | undefined,
    file: ApiFile,
): { code: string; titles: string[] } {
    const parts = (sectionPath ?? [])
        .filter(
            (part) =>
                part.length > 0 &&
                part !== "Drawing" &&
                part !== "Section" &&
                part !== "_" &&
                !/^[*_]?\[\d+\]$/.test(part),
        )
        .map(readablePathPart)
        .filter((part): part is string => part !== null);
    const pathCode = parts[0]?.match(SHEET_CODE)?.[1];
    const code = pathCode ?? file.fileName.replace(/\.[^.]+$/, "").split(/\s+-\s+/, 1)[0];
    const rest = pathCode ? [parts[0].slice(pathCode.length), ...parts.slice(1)] : parts;
    const titles: string[] = [];
    for (const part of rest) {
        let title = part.trim();
        if (title.toLowerCase().startsWith(`${code.toLowerCase()} `)) {
            title = title.slice(code.length).trim();
        }
        title = title
            .split("\n", 1)[0]
            .trim()
            .replace(/^\((.*)\)$/, "$1")
            .trim();
        const seen = titles.some((known) => known.toLowerCase() === title.toLowerCase());
        if (title && title.toLowerCase() !== code.toLowerCase() && !seen) titles.push(title);
    }
    return { code, titles };
}

export function filterScheduleSectionPath(sectionPath: string[] | undefined): string[] {
    return (sectionPath ?? []).filter(
        (part) =>
            part &&
            part !== "_" &&
            part !== "Schedule" &&
            part !== "Item" &&
            !part.startsWith("_["),
    );
}

export function isScheduleReference(file: ApiFile, reference: ApiReference): boolean {
    return (
        file.fileType === ApiFileTypes.Schedule ||
        reference.documentType === ApiDocumentTypes.Schedule
    );
}

export function getReferenceDescription(reference: ApiReference, file: ApiFile): string {
    if (isScheduleReference(file, reference)) {
        const sectionPath = filterScheduleSectionPath(reference.sectionPath);
        return sectionPath.length > 0
            ? `Schedule Activity: ${sectionPath.join(" > ")}`
            : `Schedule: ${file.fileName}`;
    }

    if (!reference.sectionPath || reference.sectionPath.length === 0) {
        return file.fileName;
    }

    const filtered = reference.sectionPath
        .filter((part) => part.length > 0 && part !== "_" && !/^[*_]?\[\d+\]$/.test(part))
        .map(readablePathPart)
        .filter((part): part is string => part !== null);
    if (filtered.length === 0) return file.fileName;

    if (filtered.length === 1 && categorizeReference(reference, file) === "drawing") {
        const identifier = file.fileName.replace(/\.[^.]+$/, "").split(/\s+-\s+/, 1)[0];
        return filtered[0] === identifier ? identifier : `${identifier} | ${filtered[0]}`;
    }
    if (filtered.length < 2) return file.fileName;

    const secondPart = filtered[1];
    const firstPartPrefix = filtered[0];
    const repeatsFirstPart =
        secondPart.slice(0, firstPartPrefix.length).toLowerCase() ===
            firstPartPrefix.toLowerCase() &&
        !/[A-Z0-9]/i.test(secondPart[firstPartPrefix.length] ?? "");
    const firstPart = repeatsFirstPart ? secondPart : `${filtered[0]} ${secondPart}`;
    const remainingParts = filtered.slice(2).join(" | ");
    return remainingParts ? `${firstPart} | ${remainingParts}` : firstPart;
}

export function getReferenceText(reference: ApiReference, file: ApiFile, maxLength = 40): string {
    if (!reference.sectionPath || reference.sectionPath.length === 0) {
        return "Reference";
    }

    if (isScheduleReference(file, reference)) {
        const sectionPath = filterScheduleSectionPath(reference.sectionPath);
        const taskCode = sectionPath.length > 0 ? sectionPath[0] : "Activity";
        return taskCode.length > maxLength ? taskCode.slice(0, maxLength - 1) + "…" : taskCode;
    }

    const category = categorizeReference(reference, file);
    const isSpec = category === "spec";
    const isDrawing = category === "drawing";

    if (attachmentKind(file.fileName, file.fileType) === "image") {
        return file.fileName.length > maxLength
            ? file.fileName.slice(0, maxLength - 1) + "…"
            : file.fileName;
    }

    let sectionPath: string[];
    if (isSpec || isDrawing) {
        sectionPath = reference.sectionPath.filter(
            (part) =>
                part.length > 0 &&
                part !== "Drawing" &&
                part !== "_" &&
                part !== "Section" &&
                !/^[*_]?\[\d+\]$/.test(part),
        );
    } else {
        sectionPath = reference.sectionPath.filter(
            (part) => part.length > 0 && !/^[*_]?\[\d+\]$/.test(part),
        );
    }

    if (sectionPath.length === 0) {
        const bounds = Object.values(reference.bounds);
        const page = bounds.length > 0 ? (bounds[0] as ApiBounds).page + 1 : 0;
        return page > 0 ? `Page ${page}` : "Reference";
    }

    if (isDrawing) {
        const { code, titles } = readDrawingPath(reference.sectionPath, file);
        const title = titles[titles.length - 1];
        const label = title ? `${code} ‣ ${title}` : code;
        return label.length > maxLength ? label.slice(0, maxLength - 1) + "…" : label;
    }

    const normalizedFileName = file.fileName
        .trim()
        .toLowerCase()
        .replace(/\.[a-z0-9]+$/, "");
    const sectionPathCandidates = sectionPath
        .map((part) => part.replace(/[_[\]]/g, ""))
        .filter((part) => {
            const lower = part.toLowerCase();
            const normalizedPart = part
                .trim()
                .toLowerCase()
                .replace(/\.[a-z0-9]+$/, "");
            return (
                !Object.values(ApiDocumentTypes).some(
                    (documentType) => documentType.toLowerCase() === lower,
                ) && normalizedPart !== normalizedFileName
            );
        });
    const specificPart = sectionPathCandidates.slice(-2).join(" · ");

    let text: string;
    if (!isSpec) {
        const fileNamePrefix = file.fileName.slice(0, 16);
        text = specificPart ? `${fileNamePrefix} ‣ ${specificPart}` : fileNamePrefix;
    } else if (sectionPath.length === 1) {
        text = sectionPath[0];
    } else if (sectionPath.length >= 3) {
        const title = sectionPath[1];
        let firstId = "";
        for (let index = 2; index < sectionPath.length; index += 1) {
            if (sectionPath[index] !== title && sectionPath[index].length > 0) {
                firstId = sectionPath[index];
                break;
            }
        }
        text = firstId ? `${sectionPath[0]} ‣ ${firstId} ${title}` : `${sectionPath[0]} ‣ ${title}`;
    } else {
        text = `${sectionPath[0]} ‣ ${sectionPath[1]}`;
    }

    return text.length > maxLength ? text.slice(0, maxLength - 1) + "…" : text;
}
